import { useEffect, useState, useCallback } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Loader2, Send, FileDown, ShieldCheck, Clock, Download, BookOpen,
} from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

// Historial de Cortes-Z con reenvío del certificado firmado.
// Abierto desde RegisterDashboardCard. Por fila:
//   • Z-number, fecha, ventas, hash corto
//   • Estado del último email (sent/failed/skipped) con tooltip
//   • Botón "Reenviar" con email opcional
//   • Botón "Descargar JSON" (genera el canónico vía API resend)

function StatusBadge({ status, to }) {
  if (status === "sent") {
    return <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 text-[10px]" title={`Enviado a ${to || "—"}`}>
      Enviado
    </Badge>;
  }
  if (status === "failed") {
    return <Badge variant="outline" className="bg-rose-50 border-rose-200 text-rose-700 text-[10px]">
      Falló
    </Badge>;
  }
  if (status === "skipped") {
    return <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-700 text-[10px]">
      Omitido
    </Badge>;
  }
  return <Badge variant="outline" className="bg-muted/50 border-border text-muted-foreground text-[10px]">
    Sin enviar
  </Badge>;
}

export default function ZClosesHistoryDialog({
  open, onClose, restaurantId, register,
}) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [logs, setLogs] = useState({}); // z_close_id → last log doc
  const [resendingId, setResendingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingBook, setDownloadingBook] = useState(false);
  const [overrideEmail, setOverrideEmail] = useState({});

  const load = useCallback(async () => {
    if (!open || !restaurantId || !register?.register_id) return;
    setLoading(true);
    try {
      const r = await api.listZCloses(restaurantId, register.register_id, { limit: 30 });
      const zs = r.z_closes || [];
      setRows(zs);
      // Fetch latest email-log per Z-close in parallel
      const logMap = {};
      await Promise.all(zs.map(async (z) => {
        try {
          const lg = await api.getZCertEmailLog(restaurantId, z.z_close_id);
          if (lg?.logs?.length) logMap[z.z_close_id] = lg.logs[0];
        } catch { /* ignore */ }
      }));
      setLogs(logMap);
    } catch (e) {
      toast.error(e?.message || "Error al cargar historial");
    }
    finally { setLoading(false); }
  }, [open, restaurantId, register]);

  useEffect(() => { load(); }, [load]);

  const handleResend = async (z, emailTo) => {
    setResendingId(z.z_close_id);
    try {
      const r = await api.resendZCertificate(restaurantId, z.z_close_id, { emailTo: emailTo || null });
      toast.success(`Certificado enviado a ${r.to}`);
      // Refresh log row
      const lg = await api.getZCertEmailLog(restaurantId, z.z_close_id);
      setLogs((prev) => ({ ...prev, [z.z_close_id]: lg?.logs?.[0] || null }));
      setOverrideEmail((prev) => ({ ...prev, [z.z_close_id]: "" }));
    } catch (e) {
      toast.error(e?.message || "Error al reenviar");
    }
    finally { setResendingId(null); }
  };

  const handleDownload = async (z) => {
    setDownloadingId(z.z_close_id);
    try {
      const { blob, filename } = await api.downloadZCertificate(restaurantId, z.z_close_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Descargado ${filename}`);
    } catch (e) {
      toast.error(e?.message || "Error al descargar");
    }
    finally { setDownloadingId(null); }
  };

  const handleDownloadBook = async () => {
    setDownloadingBook(true);
    try {
      const year = new Date().getFullYear();
      const { blob, filename } = await api.downloadAnnualZBook(restaurantId, {
        year, registerId: register?.register_id,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Libro anual ${year} descargado`);
    } catch (e) {
      toast.error(e?.message || "Error al descargar el libro");
    }
    finally { setDownloadingBook(false); }
  };

  const fmt = (v) => typeof v === "number" ? `${v.toFixed(2)}€` : "—";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="z-closes-history-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between gap-3">
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-emerald-700" />
              Historial Cortes-Z · {register?.name}
            </DialogTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-7 gap-1 mr-6"
              onClick={handleDownloadBook}
              disabled={downloadingBook}
              data-testid="z-annual-book-download"
              title="Libro de Cortes-Z del ejercicio en curso (PDF con cadena SHA-256)"
            >
              {downloadingBook ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <BookOpen className="h-3 w-3" />
              )}
              Libro anual PDF
            </Button>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Cargando…
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8 text-sm text-muted-foreground">
            Esta caja aún no tiene Cortes-Z emitidos.
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {rows.map((z) => {
              const log = logs[z.z_close_id];
              const shortHash = (z.content_hash || "").slice(0, 16);
              const totals = z.totals || {};
              const busy = resendingId === z.z_close_id;
              return (
                <div key={z.z_close_id}
                  className="p-3 rounded border bg-card space-y-2"
                  data-testid={`z-close-row-${z.z_close_id}`}>
                  <div className="flex items-start gap-3">
                    <div className="font-mono text-sm font-bold text-violet-700 tabular-nums mt-0.5 min-w-[60px]">
                      Z-{String(z.z_number).padStart(3, "0")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {z.session_date}
                        {z.shift_id && <span className="text-[10px] bg-muted px-1.5 rounded">{z.shift_id}</span>}
                        <StatusBadge status={log?.status} to={log?.to} />
                      </div>
                      <div className="mt-1 text-sm font-semibold tabular-nums">
                        {fmt(totals.grand_total)}
                        <span className="ml-2 text-[11px] text-muted-foreground font-normal">
                          {totals.count_sales || 0} tickets
                        </span>
                        {totals.discrepancy != null && Math.abs(Number(totals.discrepancy)) >= 0.01 && (
                          <span className={`ml-2 text-[11px] font-medium ${totals.discrepancy < 0 ? "text-rose-700" : "text-amber-700"}`}>
                            · descuadre {fmt(totals.discrepancy)}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 font-mono text-[10px] text-muted-foreground break-all">
                        SHA-256: {shortHash}…
                      </div>
                    </div>
                  </div>

                  {/* Resend controls */}
                  <div className="flex items-center gap-2 border-t pt-2">
                    <Input
                      type="email"
                      value={overrideEmail[z.z_close_id] || ""}
                      onChange={(e) => setOverrideEmail((p) => ({ ...p, [z.z_close_id]: e.target.value }))}
                      placeholder="Reenviar a otro email (opcional)"
                      className="h-7 text-[11px] flex-1"
                      disabled={busy}
                      data-testid={`z-close-override-${z.z_close_id}`}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 gap-1"
                      onClick={() => handleResend(z, overrideEmail[z.z_close_id])}
                      disabled={busy}
                      data-testid={`z-close-resend-${z.z_close_id}`}
                    >
                      {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                      Reenviar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 gap-1"
                      onClick={() => handleDownload(z)}
                      disabled={downloadingId === z.z_close_id}
                      data-testid={`z-close-download-${z.z_close_id}`}
                      title="Descargar certificado JSON firmado"
                    >
                      {downloadingId === z.z_close_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Download className="h-3 w-3" />
                      )}
                      JSON
                    </Button>
                  </div>

                  {log?.status === "failed" && (
                    <div className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">
                      Último intento falló: {log.reason || "sin detalle"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
